import {FC, FocusEvent, useMemo} from 'react';
import {Formik, Field, Form} from 'formik';
import {Box, Typography} from '@mui/material';
import {FIELD, FIELD_LABEL, PLACEHOLDER, H5} from '@/utils/constants';
import Input from '@/shared/components/Inputs/Input';
import {getErrorMessage, setRequiredLabel} from '@/utils/helpers';
import {useAppDispatch} from '@/store/hooks';
import {changeProfile} from '@/store/organization';
import {validationSchema, defaultInitialValues, IFormProps, IOrganization} from './formConfiguration';
import {ProfileGeneralForm} from './ProfileGeneralForm.style';
import {toast} from 'react-toastify';

const ProfileGeneral: FC<IFormProps> = ({organization}) => {
  const dispatch = useAppDispatch();

  const formikInitialValues = useMemo(() => {
    const initialValues = organization
      ? {
          organizationName: organization.name,
          organizationAddress: organization.address,
          email: organization.email,
          taxIdNumber: organization.taxIdNumber,
          phoneNumber: organization.phoneNumber || '',
          city: organization.city,
          state: organization.state,
          zip: organization.zip,
        }
      : null;
    return initialValues || defaultInitialValues;
  }, [organization]);

  const updateProfile = async (event: FocusEvent<HTMLInputElement>, param: string) => {
    const {id, name, address, email, taxIdNumber, phoneNumber, logo, city, state, zip} = organization;
    const newValue = event.target.value.trim();

    const REQUIRED_FIELDS = [FIELD.name, FIELD.address, FIELD.email, FIELD.city, FIELD.state, FIELD.zip];
    if (REQUIRED_FIELDS.includes(param) && !newValue) return;

    const params = {
      id,
      name,
      address,
      email,
      taxIdNumber,
      phoneNumber,
      city,
      state,
      zip,
      [param]: newValue,
      logo: logo?.split('/')[4]?.split('?')[0] || null,
    };

    try {
      if (organization[param as keyof IOrganization] === newValue) return;

      await dispatch(changeProfile(params)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Formik
      initialValues={formikInitialValues}
      validationSchema={validationSchema()}
      onSubmit={() => {}}
      enableReinitialize={true}
    >
      <Box>
        <Form style={ProfileGeneralForm.widget}>
          <Box sx={{width: '100%'}}>
            <Typography variant={H5} sx={[ProfileGeneralForm.formTitle, ProfileGeneralForm.widgetTitle]}>
              Contact Information
            </Typography>
            <Box sx={ProfileGeneralForm.formContainer}>
              {' '}
              <Box sx={ProfileGeneralForm.fieldContainer}>
                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.organizationName}
                    name={FIELD.organizationName}
                    placeholder={PLACEHOLDER.organizationName}
                    label={setRequiredLabel(FIELD_LABEL.organizationName)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.name)}
                  />
                </Box>

                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.email}
                    name={FIELD.email}
                    placeholder={PLACEHOLDER.email}
                    label={setRequiredLabel(FIELD_LABEL.email)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.email)}
                  />
                </Box>

                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.taxIdNumber}
                    name={FIELD.taxIdNumber}
                    placeholder={PLACEHOLDER.taxIdNumber}
                    label={FIELD_LABEL.taxIdNumber}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.taxIdNumber)}
                  />
                </Box>

                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.phoneNumber}
                    name={FIELD.phoneNumber}
                    placeholder={PLACEHOLDER.phoneNumber}
                    label={FIELD_LABEL.phoneNumber}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.phoneNumber)}
                  />
                </Box>
              </Box>
              <Box sx={ProfileGeneralForm.fieldContainer}>
                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.organizationAddress}
                    name={FIELD.organizationAddress}
                    placeholder={PLACEHOLDER.organizationAddress}
                    label={setRequiredLabel(FIELD_LABEL.organizationAddress)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.address)}
                  />
                </Box>
                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.city}
                    name={FIELD.city}
                    placeholder={PLACEHOLDER.city}
                    label={setRequiredLabel(FIELD_LABEL.city)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.city)}
                  />
                </Box>
                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.state}
                    name={FIELD.state}
                    placeholder={PLACEHOLDER.state}
                    label={setRequiredLabel(FIELD_LABEL.state)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.state)}
                  />
                </Box>
                <Box sx={ProfileGeneralForm.field}>
                  <Field
                    id={FIELD.zip}
                    name={FIELD.zip}
                    placeholder={PLACEHOLDER.zip}
                    label={setRequiredLabel(FIELD_LABEL.zip)}
                    component={Input}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => updateProfile(event, FIELD.zip)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Form>
      </Box>
    </Formik>
  );
};

export default ProfileGeneral;
